/**
 * One call at a time, for the whole app.
 *
 * The pieces below this were each built to be driven by somebody: the store
 * sends and receives the four `call` frames, `CallSession` is the media, and
 * `CallOverlay` draws whatever it is handed. This is the somebody. It lives
 * above the router because a ring is not a property of the page that happens to
 * be open — a call arriving while you are reading a board has to ring.
 *
 * ── Two halves that are deliberately not one ────────────────────────────────
 * The **ephemeral** half (`ring` / `join` / `decline` / `leave`) is what the
 * other end sees happening now, and reaches only a connected client. The
 * **durable** half is one `callend` row, written once per call id. They are
 * different mechanisms with different failure modes, and the rule that joins
 * them changed on 2026-09-17: the row is written by whoever `DELETE …/session`
 * answered `last` to — the caller when nobody ever picked up, whoever turns the
 * lights out when somebody did. The caller cannot know the second of those in a
 * group, which is why it is no longer the caller's job.
 *
 * ── The three rules a ring obeys ────────────────────────────────────────────
 * From `relay/docs/protocol.md` §7b, which is the same paragraph the desktop
 * implements:
 *
 * 1. **Already in a call.** A DM still declines — the other end is one person
 *    waiting for an answer and silence is not one. A group does not: the call
 *    bar in that conversation is the invitation, and a decline from somebody
 *    mid-call is noise.
 * 2. **Do not disturb.** Shows nothing, sounds nothing, sends nothing, and the
 *    caller's ring times out as missed — which is the truth. This app draws
 *    four presences and `busy` is the one do-not-disturb folds onto
 *    (`payload.ts`, `presenceFromWire`), so `busy` is what it reads.
 * 3. **A decline is a fact, not an ending.** In a group the outgoing call ends
 *    when everybody rung has declined, or on the 30-second timeout. Somebody
 *    else's `leave` never ends yours in a group, and always does in a DM.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react';
import { store } from '../lib/relay/store';
import { openCallSession, type LiveCallSession } from '../lib/calls/session';
import { createRingTone } from '../lib/calls/ringtone';
import { joinDevices, readPrefs } from '../lib/calls/prefs';
import { CallOverlay } from '../components/call/CallOverlay';
import { everybodyDeclined, leaveEffect, ringAction } from './ring';
import type { CallOutcome, Presence } from '../lib/relay/types';

/** The desktop's `kRingTimeoutMs`. Long enough to reach a phone in a pocket,
 *  short enough that a call nobody is near stops pretending. */
const RING_TIMEOUT_MS = 30_000;

export interface ActiveCall {
  conversationId: string;
  callId: string;
  /** Who placed it. A ring you did not start is answered, not cancelled. */
  direction: 'incoming' | 'outgoing';
  /** The name on the screen while there is no tile yet. */
  peerName: string;
  /** True while an incoming call is still an invitation. */
  ringing: boolean;
  session: LiveCallSession;
}

export interface Calls {
  /** The call being offered, placed, or in progress. Null when there is none. */
  active: ActiveCall | null;
  /** Place a call in a conversation: a `ring` on the lane, and a seat. */
  start(conversationId: string, opts?: { camera?: boolean }): void;
  /** Take a seat in a call that is already running — the call bar's Join. */
  join(conversationId: string, callId: string): void;
  answer(): void;
  decline(): void;
  hangUp(): void;
}

/** Everything one call needs to know about itself, mutable and outside React:
 *  a socket frame arrives whenever it arrives, and a closure over stale state
 *  is how a decline ends the wrong call. */
interface Held {
  conversationId: string;
  callId: string;
  direction: 'incoming' | 'outgoing';
  peerName: string;
  camera: boolean;
  ringing: boolean;
  /** Somebody else took a seat or sent `join`. What tells Missed from
   *  Answered, and what stops a late decline ending a live call. */
  connected: boolean;
  /** The outcome if it ends now and nobody ever connected. */
  hint: CallOutcome;
  /** Who was rung, and who has said no. The outgoing call ends when the
   *  second contains the first — in a DM that is one person. */
  rung: Set<string>;
  declined: Set<string>;
  session: LiveCallSession;
  presenceBefore: Presence | null;
  timeout: number | null;
  offs: (() => void)[];
  done: boolean;
}

const CallsContext = createContext<Calls | null>(null);

/** The fields every call starts with, whichever end placed it. */
type Blank = Omit<Held, 'conversationId' | 'callId' | 'direction' | 'peerName' | 'session'>;

const blank = (): Blank => ({
  camera: false,
  ringing: false,
  connected: false,
  hint: 'missed',
  rung: new Set<string>(),
  declined: new Set<string>(),
  presenceBefore: null,
  timeout: null,
  offs: [],
  done: false
});

/** Every call id this page has already written a `callend` for. Module level
 *  rather than per-provider: it is the spent set the protocol asks a client to
 *  keep, and a remount must not spend an id twice. */
const spent = new Set<string>();

export function CallProvider({ children }: { children: React.ReactNode }) {
  const held = useRef<Held | null>(null);
  const [version, bump] = useReducer((n: number) => n + 1, 0);
  const tone = useMemo(() => createRingTone(), []);

  // ── the pieces every path shares ──────────────────────────────────────────

  const clear = useCallback(
    (call: Held) => {
      if (call.done) return;
      call.done = true;
      tone.stop();
      if (call.timeout !== null) window.clearTimeout(call.timeout);
      for (const off of call.offs) off();
      if (call.presenceBefore) store.setPresence(call.presenceBefore);
      if (held.current === call) {
        held.current = null;
        bump();
      }
    },
    [tone]
  );

  /**
   * The seat has been given back, and the server has said whether it was the
   * last one. Exactly one row per call id, and the set is what makes it exact:
   * a caller whose call rang out has already written one by the time a late
   * `leave` comes back around.
   */
  const finish = useCallback(
    (call: Held, last: boolean, since: number, callId: string) => {
      const id = callId || call.callId;
      if (last && !spent.has(id)) {
        spent.add(id);
        const outcome: CallOutcome = call.connected ? 'answered' : call.hint;
        store.postCallEnd(call.conversationId, id, outcome, {
          camera: call.camera,
          durationSeconds:
            outcome === 'answered' && since > 0 ? (Date.now() - since) / 1000 : 0
        });
      }
      clear(call);
    },
    [clear]
  );

  /** End a call this browser is in or placing. The `leave` frame goes either
   *  way; the seat is only given back when there is one. */
  const end = useCallback(
    (call: Held, hint?: CallOutcome) => {
      if (call.done) return;
      if (hint && !call.connected) call.hint = hint;
      tone.stop();
      if (call.timeout !== null) {
        window.clearTimeout(call.timeout);
        call.timeout = null;
      }
      store.hangUp(call.conversationId, call.callId);
      if (call.session.status === 'idle' || call.session.status === 'ended') {
        // Never took a seat — an invitation refused, or a join that failed.
        // Nothing to leave, and nobody the server would answer `last` to.
        clear(call);
        return;
      }
      void call.session.leave();
    },
    [clear, tone]
  );

  /**
   * Take the seat, with the choices Settings saved.
   *
   * The devices go in with the join because they are constraints on tracks that
   * are about to be opened; the background follows it, because it replaces a
   * camera track that has to exist first.
   */
  const seat = useCallback((call: Held) => {
    const prefs = readPrefs();
    void call.session
      .join({ callId: call.callId, camera: call.camera, devices: joinDevices(prefs) })
      .then(() => {
        if (prefs.background.kind !== 'none') void call.session.setBackground(prefs.background);
        void call.session.setAudioProcessing?.({
          noiseSuppression: prefs.noiseSuppression,
          echoCancellation: prefs.echoCancellation,
          autoGainControl: prefs.autoGainControl
        });
      });
  }, []);

  /** A call this browser now owns: the session's events, and the screen. */
  const begin = useCallback(
    (call: Held) => {
      call.offs.push(
        call.session.on('left', ({ last, since, callId }) => finish(call, last, since, callId)),
        call.session.on('statusChanged', (status) => {
          if (status === 'inCall' && !call.presenceBefore) {
            // In a call is a fact other people can act on, so it goes on the
            // lane. The choice made before it is restored when the call ends.
            call.presenceBefore = store.me()?.presence ?? 'online';
            store.setPresence('busy');
          }
          if (status === 'ended') clear(call);
          bump();
        }),
        call.session.on('rosterChanged', (peers) => {
          if (peers.length > 0) {
            // Somebody is in the room. The `join` frame usually says so first,
            // but it is an ephemeral and the seat is not: a call that connected
            // without one must not be hung up by the ring timeout.
            call.connected = true;
            call.hint = 'answered';
            if (call.timeout !== null) {
              window.clearTimeout(call.timeout);
              call.timeout = null;
            }
          }
          bump();
        })
      );
      held.current = call;
      bump();
    },
    [clear, finish]
  );

  // ── what a screen can ask for ─────────────────────────────────────────────

  const start = useCallback(
    (conversationId: string, opts?: { camera?: boolean }) => {
      if (held.current) return;
      const conversation = store.conversations().find((c) => c.id === conversationId);
      const me = store.me();
      const camera = opts?.camera === true;
      // The first joiner names the call for everybody. A later one's name is
      // ignored and the stored id comes back on the 201.
      const callId = `call_${Date.now().toString(36)}${Math.floor(Math.random() * 4096).toString(36)}`;

      store.ring(conversationId, callId, { camera });

      const call: Held = {
        ...blank(),
        conversationId,
        callId,
        direction: 'outgoing',
        peerName: conversation?.title ?? '',
        camera,
        rung: new Set((conversation?.members ?? []).filter((id) => id !== me?.id)),
        session: openCallSession({ kind: 'call', id: conversationId })
      };
      begin(call);
      seat(call);
      call.timeout = window.setTimeout(() => end(call, 'missed'), RING_TIMEOUT_MS);
    },
    [begin, end, seat]
  );

  const join = useCallback(
    (conversationId: string, callId: string) => {
      if (held.current) return;
      const conversation = store.conversations().find((c) => c.id === conversationId);
      // Late joining is `join` on the lane under the call state's id, then a
      // seat — the same two steps as answering, without the invitation.
      store.answer(conversationId, callId);
      const call: Held = {
        ...blank(),
        conversationId,
        callId,
        direction: 'outgoing',
        peerName: conversation?.title ?? '',
        connected: true,
        hint: 'answered',
        session: openCallSession({ kind: 'call', id: conversationId })
      };
      begin(call);
      seat(call);
    },
    [begin, seat]
  );

  const answer = useCallback(() => {
    const call = held.current;
    if (!call || !call.ringing) return;
    tone.stop();
    call.ringing = false;
    call.connected = true;
    call.hint = 'answered';
    store.answer(call.conversationId, call.callId);
    seat(call);
    bump();
  }, [seat, tone]);

  const decline = useCallback(() => {
    const call = held.current;
    if (!call) return;
    tone.stop();
    // Declining writes nothing. The end that placed the call writes the row,
    // which is what makes one call leave one row rather than two disagreeing.
    store.decline(call.conversationId, call.callId);
    clear(call);
  }, [clear, tone]);

  const hangUp = useCallback(() => {
    const call = held.current;
    if (call) end(call);
  }, [end]);

  // ── the four frames ───────────────────────────────────────────────────────

  useEffect(() => {
    return store.onCallFrames((event) => {
      const call = held.current;

      if (event.fromSelf) {
        // Another of our own devices. The only thing it can say is that this
        // invitation has been dealt with somewhere else; a `ring` from
        // ourselves is our own outgoing call coming back.
        if (
          call &&
          call.ringing &&
          call.callId === event.callId &&
          (event.action === 'join' || event.action === 'decline')
        ) {
          clear(call);
        }
        return;
      }

      if (event.action === 'ring') {
        const conversation = store.conversations().find((c) => c.id === event.conversationId);
        const action = ringAction({
          engaged: call !== null,
          kind: conversation?.kind,
          presence: store.me()?.presence ?? 'online'
        });
        // Refused rather than stacked: a queue of ringing calls is a decision
        // nobody has made, and answering the wrong one is worse than not being
        // offered it.
        if (action === 'decline') store.decline(event.conversationId, event.callId);
        if (action !== 'ring') return;

        const caller =
          store.people([event.fromUserId])[0]?.name ?? event.fromName ?? '';
        const roomy = conversation?.kind === 'group' || conversation?.kind === 'channel';

        const incoming: Held = {
          ...blank(),
          conversationId: event.conversationId,
          callId: event.callId,
          direction: 'incoming',
          peerName: roomy ? conversation.title : caller,
          camera: event.camera === true,
          ringing: true,
          session: openCallSession({ kind: 'call', id: event.conversationId })
        };
        begin(incoming);
        tone.start();
        // Stops ringing and writes nothing: the caller's own timeout writes the
        // record, and a callee that wrote one too would double every missed
        // call.
        incoming.timeout = window.setTimeout(() => clear(incoming), RING_TIMEOUT_MS);
        return;
      }

      // Everything below is about a call already under way. The id is what
      // makes a late frame harmless.
      if (!call || call.callId !== event.callId) return;

      if (event.action === 'join') {
        call.connected = true;
        call.hint = 'answered';
        if (call.timeout !== null) {
          window.clearTimeout(call.timeout);
          call.timeout = null;
        }
        bump();
        return;
      }

      if (event.action === 'decline') {
        if (call.connected || call.direction !== 'outgoing') return;
        call.declined.add(event.fromUserId);
        if (everybodyDeclined(call.rung, call.declined)) end(call, 'declined');
        return;
      }

      if (event.action === 'leave') {
        const kind = store.conversations().find((c) => c.id === call.conversationId)?.kind;
        const effect = leaveEffect({ ringing: call.ringing, kind });
        if (effect === 'stopRinging') clear(call);
        if (effect === 'hangUp') end(call);
      }
    });
  }, [begin, clear, end, tone]);

  /**
   * The `callstate` frame, handed to the session.
   *
   * It carries no SFU session id, so it cannot be pulled from: all it does is
   * notice this device's seat has gone and bring the next roster read forward.
   * The roster is compared before handing it over because the store notifies on
   * every typing dot and a poll per keystroke is a poll too many.
   */
  useEffect(() => {
    let last = '';
    return store.subscribe(() => {
      const call = held.current;
      if (!call || call.session.status !== 'inCall') return;
      const state = store.callState(call.conversationId);
      if (!state) return;
      const signature = state.roster.map((seat) => seat.deviceId).join(',');
      if (signature === last) return;
      last = signature;
      call.session.applyCallState(state.roster);
    });
  }, []);

  // A page being closed mid-call gives the seat back. Without it the roster
  // holds a row for a browser that is gone until the 45-second sweep, and
  // every later joiner pulls a track nobody is sending.
  useEffect(() => {
    const onUnload = () => {
      const call = held.current;
      if (call && !call.done) void call.session.leave();
    };
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
  }, []);

  const call = held.current;
  const value = useMemo<Calls>(
    () => ({
      active: call
        ? {
            conversationId: call.conversationId,
            callId: call.callId,
            direction: call.direction,
            peerName: call.peerName,
            ringing: call.ringing,
            session: call.session
          }
        : null,
      start,
      join,
      answer,
      decline,
      hangUp
    }),
    // `version` is the dependency that matters: the record is mutated in place
    // and its identity does not change when `ringing` does, so the counter is
    // what says it moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [call, version, start, join, answer, decline, hangUp]
  );

  return (
    <CallsContext.Provider value={value}>
      {children}
      {value.active && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 40,
            display: 'flex',
            background: 'var(--app-ground)'
          }}
        >
          <CallOverlay
            session={value.active.session}
            direction={value.active.direction}
            peerName={value.active.peerName}
            onAnswer={answer}
            onDecline={decline}
            onHangUp={hangUp}
          />
        </div>
      )}
    </CallsContext.Provider>
  );
}

/** The call controller. Outside the provider — the guest door — there is no
 *  call to place and nothing rings, so the methods do nothing rather than
 *  throwing at a component that was only asking. */
export function useCalls(): Calls {
  return useContext(CallsContext) ?? IDLE;
}

const IDLE: Calls = {
  active: null,
  start: () => {},
  join: () => {},
  answer: () => {},
  decline: () => {},
  hangUp: () => {}
};
